import React, { useState, useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import * as d3 from 'd3';
import styled from 'styled-components';
import axios from 'axios';
import { CSVLink } from "react-csv";
import Cookies from 'js-cookie';
import * as htmlToImage from 'html-to-image';
import ChatAssistant from '../Components/ChatAssistant';

// Styled Components
const MapContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const MapElement = styled.div`
  height: 100%;
  width: 100%;
`;

const ControlPanel = styled.div`
  width: 300px;
  background-color: #f8f9fa;
  padding: 20px;
  overflow-y: auto;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ControlSection = styled.div`
  background-color: #fff;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SubTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: #3498db;
`;

const SliderContainer = styled.div`
  margin-bottom: 15px;
`;

const SliderInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  margin-top: 3px;
  color: #666;
`;

const SliderValue = styled.span`
  font-weight: bold;
  color: #3498db;
`;

const ColorPickerContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const ColorInput = styled.input`
  width: 50px;
  height: 30px;
  padding: 0;
  border: none;
`;

const CheckboxGroup = styled.div`
  margin-bottom: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: auto;
  margin-right: 8px;
  margin-bottom: 0;
`;

const Legend = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const LegendGradient = styled.div`
  width: 200px;
  height: 20px;
  margin-bottom: 5px;
`;

const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
`;

const DownloadContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MainContent = styled.div`
  flex: 1;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Reusing existing Button from StyledComponents
const Button = styled.button`
  background-color: var(--primary-color);
  border-radius: 10px;
  border: none;
  font-size: 20px;
  padding: 7px 10px;
  width: 13rem;
  margin: 1rem auto;
  cursor: pointer;
  color: var(--light-color);
`;

const Slider = styled.input`
  width: 100%;
  padding: 8px 0;
`;

const Heatmap2 = ({ selectedProjectId, selectedQuestionId }) => {
    // State for application data and settings
    const [appState, setAppState] = useState({
        data: [],
        rawAnswers: [], // Added this to store the original answer data
        settings: {
            radiusMultiplier: 0.4,
            contourSmoothness: 50,
            numShades: 10,
            lowColor: '#FFFA00',
            highColor: '#FF0000'
        },
        layerVisibility: {
            baseMap: true,
            contourLines: true,
            points: true
        }
    });

    // Refs for map and layers
    const mapRef = useRef(null);
    const mapLayersRef = useRef({
        baseMap: null,
        points: null,
        heatmap: null,
        contoursFill: null,
        contourLines: null
    });

    // Initialize the map
    const initMap = () => {
        if (!mapRef.current) {
            // Initialize with a default view that will be updated once data is loaded
            mapRef.current = L.map('map').setView([37.7749, -122.4194], 10);

            mapLayersRef.current.baseMap = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(mapRef.current);

            mapLayersRef.current.points = L.layerGroup().addTo(mapRef.current);
            mapLayersRef.current.contoursFill = L.layerGroup().addTo(mapRef.current);
            mapLayersRef.current.contourLines = L.layerGroup().addTo(mapRef.current);

            updateLegendGradient();
        }
    };

    // Get color for intensity value
    const getColorForIntensity = (intensity) => {
        const hexToRgb = hex => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null;
        };

        const lowRgb = hexToRgb(appState.settings.lowColor);
        const highRgb = hexToRgb(appState.settings.highColor);

                        // Make sure intensity is clamped between 0 and 1
                const safeIntensity = Math.max(0, Math.min(1, intensity));
                
                const r = Math.floor(lowRgb.r + (highRgb.r - lowRgb.r) * safeIntensity);
                const g = Math.floor(lowRgb.g + (highRgb.g - lowRgb.g) * safeIntensity);
                const b = Math.floor(lowRgb.b + (highRgb.b - lowRgb.b) * safeIntensity);

        return `rgb(${r}, ${g}, ${b})`;
    };

    // Generate gradient for heatmap
    const generateGradient = () => {
        const gradient = {};
        const steps = appState.settings.numShades;
        for (let i = 0; i <= steps; i++) {
            const ratio = i / steps;
            gradient[ratio] = getColorForIntensity(ratio);
        }
        return gradient;
    };

    // Create point markers
    const createPointMarkers = (data) => {
        data.forEach(point => {
            const marker = L.circleMarker([point.lat, point.lng], {
                radius: 4,
                fillColor: getColorForIntensity(point.intensity),
                color: '#000',
                weight: 1,
                opacity: 1,
                fillOpacity: 0.8
            });

            // Create a more informative popup with user data
            marker.bindPopup(`
        <strong>Name:</strong> ${point.name || 'Anonymous'}<br>
        <strong>Rating:</strong> ${point.originalRating} (out of ${point.maxPossibleRating})<br>
        <strong>Age:</strong> ${point.age || 'Not specified'}<br>
        <strong>Gender:</strong> ${point.gender || 'Not specified'}<br>
        <strong>Date:</strong> ${new Date(point.date).toLocaleDateString() || 'Not specified'}<br>
        <strong>Location:</strong> ${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}
      `);

            marker.addTo(mapLayersRef.current.points);
        });
    };

    // Update point markers when colors change
    const updatePointMarkers = () => {
        mapLayersRef.current.points.eachLayer(layer => {
            if (layer instanceof L.CircleMarker) {
                const intensity = layer.feature?.properties?.intensity ||
                    appState.data.find(p =>
                        p.lat === layer.getLatLng().lat &&
                        p.lng === layer.getLatLng().lng
                    )?.intensity;
                if (intensity !== undefined) {
                    layer.setStyle({
                        fillColor: getColorForIntensity(intensity)
                    });
                }
            }
        });
    };

    // Create heatmap
    const createHeatmap = (data) => {
        if (data.length === 0) return;

        const heatData = data.map(point => [
            point.lat, point.lng, point.intensity * appState.settings.radiusMultiplier * 10
        ]);

        if (!mapLayersRef.current.heatmap) {
            mapLayersRef.current.heatmap = L.heatLayer(heatData, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                max: 1.0,
                gradient: generateGradient()
            }).addTo(mapRef.current);
        } else {
            mapLayersRef.current.heatmap.setLatLngs(heatData);
        }
    };

    // Update heatmap settings
    const updateHeatmapSettings = () => {
        if (!mapLayersRef.current.heatmap) return;

        const heatData = appState.data.map(point => [
            point.lat, point.lng, point.intensity * appState.settings.radiusMultiplier * 10
        ]);

        mapLayersRef.current.heatmap.setLatLngs(heatData);
        mapLayersRef.current.heatmap.setOptions({
            gradient: generateGradient()
        });
    };

    // Smooth grid function
    const smoothGrid = (grid, smoothFactor) => {
        const height = grid.length;
        const width = grid[0].length;
        const smoothedGrid = new Array(height).fill(0).map(() => new Array(width).fill(0));

        // Simple box blur implementation
        const radius = Math.floor(smoothFactor * 5);
        const kernelSize = radius * 2 + 1;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                let sum = 0;
                let count = 0;

                for (let ky = -radius; ky <= radius; ky++) {
                    for (let kx = -radius; kx <= radius; kx++) {
                        const ny = y + ky;
                        const nx = x + kx;

                        if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                            sum += grid[ny][nx];
                            count++;
                        }
                    }
                }

                smoothedGrid[y][x] = sum / count;
            }
        }

        return smoothedGrid;
    };

    const createContours = (data) => {
        if (data.length === 0) return;

        mapLayersRef.current.contoursFill.clearLayers();
        mapLayersRef.current.contourLines.clearLayers();

        const bounds = mapRef.current.getBounds();
        const width = 100;
        const height = 100;
        const cellSize = (bounds.getEast() - bounds.getWest()) / width;

        let grid = new Array(height).fill(0).map(() => new Array(width).fill(0));

        data.forEach(point => {
            let x = Math.floor((point.lng - bounds.getWest()) / cellSize);
            let y = Math.floor((point.lat - bounds.getSouth()) / cellSize);

            // Clamp x and y to valid indices
            x = Math.max(0, Math.min(width - 1, x));
            y = Math.max(0, Math.min(height - 1, y));

            const radius = Math.floor(appState.settings.radiusMultiplier * 10); // ensure integer radius
            for (let j = Math.max(0, y - radius); j < Math.min(height, y + radius); j++) {
                for (let i = Math.max(0, x - radius); i < Math.min(width, x + radius); i++) {
                    const dist = Math.sqrt((i - x) ** 2 + (j - y) ** 2);
                    if (dist <= radius) {
                        const influence = point.intensity * Math.exp(-dist * dist / (2 * radius * radius));
                        if (Number.isInteger(j) && Number.isInteger(i) && grid[j] && typeof grid[j][i] !== "undefined") {
                            grid[j][i] += influence;
                        }
                    }
                }
            }

        });

        const maxVal = Math.max(...grid.flat());
        if (maxVal > 0) {
            grid = grid.map(row => row.map(val => val / maxVal));
        }

        if (appState.settings.contourSmoothness > 0) {
            grid = smoothGrid(grid, appState.settings.contourSmoothness / 100);
        }

        const thresholds = Array.from({ length: appState.settings.numShades }, (_, i) => (i + 1) / appState.settings.numShades);
        const values = new Float64Array(width * height);
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                values[y * width + x] = grid[y][x];
            }
        }

        const contours = d3.contours()
            .size([width, height])
            .thresholds(thresholds)(values);

        contours.forEach((feature, i) => {
            const intensity = (i + 1) / appState.settings.numShades;
            const fillColor = getColorForIntensity(intensity);

            // ❗ Defensive check for empty or malformed coordinates
            if (!feature.coordinates || !Array.isArray(feature.coordinates) || feature.coordinates.length === 0) return;

            try {
                const geoJson = {
                    type: "Feature",
                    geometry: {
                        type: "MultiPolygon",
                        coordinates: feature.coordinates.map(polygon =>
                            polygon.map(ring =>
                                ring.map(point => {
                                    const lng = bounds.getWest() + point[0] * cellSize;
                                    const lat = bounds.getSouth() + point[1] * cellSize;
                                    return [lng, lat];
                                })
                            )
                        )
                    },
                    properties: {}
                };

                L.geoJSON(geoJson, {
                    style: {
                        color: fillColor,
                        weight: 0,
                        fillOpacity: 0.5
                    }
                }).addTo(mapLayersRef.current.contoursFill);

                L.geoJSON(geoJson, {
                    style: {
                        color: '#000',
                        weight: 1,
                        opacity: 0.8,
                        fillOpacity: 0
                    }
                }).addTo(mapLayersRef.current.contourLines);
            } catch (err) {
                console.warn("Invalid contour geometry skipped:", err);
            }
        });
    };

    // Process data and update visualizations
    const processData = (data) => {
        if (!data || data.length === 0) return;

        mapLayersRef.current.points.clearLayers();
        if (mapLayersRef.current.heatmap) {
            mapRef.current.removeLayer(mapLayersRef.current.heatmap);
            mapLayersRef.current.heatmap = null;
        }
        mapLayersRef.current.contoursFill.clearLayers();
        mapLayersRef.current.contourLines.clearLayers();

        createPointMarkers(data);
        createHeatmap(data);
        createContours(data);

        if (data.length > 0) {
            const bounds = data.map(point => [point.lat, point.lng]);
            mapRef.current.fitBounds(bounds);
        }
    };

    // Update legend gradient
    const updateLegendGradient = () => {
        const legendGradient = document.getElementById('legend-gradient');
        if (!legendGradient) return;

        const steps = appState.settings.numShades;
        let gradientString = 'linear-gradient(to right';
        for (let i = 0; i <= steps; i++) {
            const ratio = i / steps;
            const color = getColorForIntensity(ratio);
            gradientString += `, ${color} ${ratio * 100}%`;
        }
        gradientString += ')';
        legendGradient.style.background = gradientString;
    };

    // Handle setting changes
    const handleSettingChange = (setting, value) => {
        setAppState(prev => ({
            ...prev,
            settings: {
                ...prev.settings,
                [setting]: value
            }
        }));

        // Update visualizations based on which setting changed
        switch (setting) {
            case 'radiusMultiplier':
                if (mapLayersRef.current.heatmap) updateHeatmapSettings();
                createContours(appState.data);
                break;
            case 'contourSmoothness':
                createContours(appState.data);
                break;
            case 'numShades':
                if (mapLayersRef.current.heatmap) updateHeatmapSettings();
                createContours(appState.data);
                updateLegendGradient();
                updatePointMarkers();
                break;
            case 'lowColor':
            case 'highColor':
                if (mapLayersRef.current.heatmap) updateHeatmapSettings();
                createContours(appState.data);
                updatePointMarkers();
                updateLegendGradient();
                break;
            default:
                break;
        }
    };

    // Handle layer visibility changes
    const handleLayerVisibilityChange = (layer, isVisible) => {
        setAppState(prev => ({
            ...prev,
            layerVisibility: {
                ...prev.layerVisibility,
                [layer]: isVisible
            }
        }));

        if (layer === 'baseMap') {
            if (isVisible) {
                mapRef.current.addLayer(mapLayersRef.current.baseMap);
            } else {
                mapRef.current.removeLayer(mapLayersRef.current.baseMap);
            }
        } else if (layer === 'contourLines') {
            if (isVisible) {
                mapRef.current.addLayer(mapLayersRef.current.contourLines);
            } else {
                mapRef.current.removeLayer(mapLayersRef.current.contourLines);
            }
        } else if (layer === 'points') {
            if (isVisible) {
                mapRef.current.addLayer(mapLayersRef.current.points);
            } else {
                mapRef.current.removeLayer(mapLayersRef.current.points);
            }
        }
    };

    // Initialize on component mount
    useEffect(() => {
        initMap();

        const interval = setInterval(() => {
            const container = document.getElementById('map');
            if (container && container.offsetHeight > 0 && container.offsetWidth > 0) {
                clearInterval(interval);
                // Map is now visible, but we'll wait for data from the backend
                // instead of using example data
            }
        }, 100);

        return () => {
            clearInterval(interval);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // Update legend gradient when settings change
    useEffect(() => {
        updateLegendGradient();
    }, [appState.settings]);

    // Fetch data when project or question changes
    useEffect(() => {
        const fetchData = async () => {
            if (!selectedProjectId) return; // 🚨 Project must exist, but question is optional

            try {
                const body = { projectId: selectedProjectId };
                if (selectedQuestionId) {
                    body.questionId = selectedQuestionId; // only add if available
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/answers/filtered`,
                    body,
                    {
                        headers: {
                            Authorization: `Bearer ${Cookies.get('token')}`
                        },
                        withCredentials: true
                    }
                );

                if (response.data.success) {
                    // Store the original answer data
                    const rawAnswerData = response.data.result;
                    
                    // Process data using user_rating instead of intensity
                    const processedData = rawAnswerData.map(item => {
                        // Get user_rating and normalize it to 0-1 range based on min_rating and max_rating
                        let rating = parseFloat(item.user_rating);
                        const minRating = parseFloat(item.min_rating) || 0;
                        const maxRating = parseFloat(item.max_rating) || 10;
                        
                        // Normalize the rating (if max > min)
                        let normalizedRating = 0.5; // default middle value
                        if (maxRating > minRating) {
                            normalizedRating = (rating - minRating) / (maxRating - minRating);
                        }
                        
                        return {
                            lat: parseFloat(item.lat),
                            lng: parseFloat(item.lng),
                            intensity: normalizedRating, // Use normalized user_rating as intensity
                            
                            // Store original values for popup displays
                            originalRating: rating,
                            minPossibleRating: minRating,
                            maxPossibleRating: maxRating,
                            
                            // Store user info for popups
                            name: item.name,
                            age: item.age,
                            gender: item.gender,
                            date: item.date
                        };
                    }).filter(item =>
                        !isNaN(item.lat) &&
                        !isNaN(item.lng) &&
                        !isNaN(item.intensity)
                    );

                    if (processedData.length > 0) {
                        // Save both the raw answers and processed data
                        setAppState(prev => ({
                            ...prev,
                            data: processedData,
                            rawAnswers: rawAnswerData
                        }));
                        processData(processedData);
                    } else {
                        console.warn("No valid data points received from backend");
                    }
                } else {
                    console.error("Backend request was not successful");
                }
            } catch (error) {
                console.error("Error fetching heatmap data:", error);
            }
        };

        fetchData();
    }, [selectedProjectId, selectedQuestionId]); // ❗ Keep both as dependencies

    const downloadSVG = async () => {
        const mapElement = document.getElementById('map');
        if (!mapElement) return;
      
        try {
          const svgData = await htmlToImage.toSvg(mapElement, {
            quality: 1,
            pixelRatio: 2,
            filter: (node) => {
              // Exclude the download buttons from the SVG
              return !(node.classList && 
                      node.classList.contains('download-container'));
            }
          });
      
          const downloadLink = document.createElement('a');
          downloadLink.href = svgData;
          downloadLink.download = 'heatmap_visualization.svg';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } catch (error) {
          console.error('Error generating SVG:', error);
        }
    };
    
    // Prepare the answer data for the ChatAssistant
    const prepareAnswersForChatbot = () => {
        if (!appState.rawAnswers || appState.rawAnswers.length === 0) {
            return "No answer data available yet";
        }
        
        // Limit to first 30 answers to avoid token overload
        return appState.rawAnswers.slice(0, 30).map(answer => {
            // Extract all relevant fields from the answer data
            const location = `(${parseFloat(answer.lat).toFixed(4)}, ${parseFloat(answer.lng).toFixed(4)})`;
            
            // Create a structured format with all available user data
            return `Location: ${location}
Name: ${answer.name || "Unknown"}
Gender: ${answer.gender || "Not specified"}
Age: ${answer.age || "Not specified"}
Rating: ${answer.user_rating || answer.rating || "Not available"}
Date: ${answer.date || "Not available"}
Maximum Rating: ${answer.max_rating || "Not available"}
Minimum Rating: ${answer.min_rating || "Not available"}
ID: ${answer.id || "Not available"}
Question ID: ${answer.question_id || "Not available"}
Project ID: ${answer.project_id || "Not available"}`;
        }).join("\n\n---\n\n"); // Add separator between entries for better readability
    };

    return (
        <Container>
            {/* Control Panel */}
            <ControlPanel>
                <ControlSection>
                    <SubTitle>Layer Visibility</SubTitle>
                    <CheckboxGroup>
                        <CheckboxLabel>
                            <CheckboxInput
                                type="checkbox"
                                checked={appState.layerVisibility.baseMap}
                                onChange={(e) => handleLayerVisibilityChange('baseMap', e.target.checked)}
                            />
                            Base Map
                        </CheckboxLabel>
                        <CheckboxLabel>
                            <CheckboxInput
                                type="checkbox"
                                checked={appState.layerVisibility.contourLines}
                                onChange={(e) => handleLayerVisibilityChange('contourLines', e.target.checked)}
                            />
                            Contour Lines
                        </CheckboxLabel>
                        <CheckboxLabel>
                            <CheckboxInput
                                type="checkbox"
                                checked={appState.layerVisibility.points}
                                onChange={(e) => handleLayerVisibilityChange('points', e.target.checked)}
                            />
                            Data Points
                        </CheckboxLabel>
                    </CheckboxGroup>
                </ControlSection>

                <ControlSection>
                    <SubTitle>Rating Visualization Settings</SubTitle>

                    <SliderContainer>
                        <label htmlFor="radius-slider">Gaussian Radius Multiplier</label>
                        <Slider
                            type="range"
                            id="radius-slider"
                            min="0.05"
                            max="2"
                            step="0.05"
                            value={appState.settings.radiusMultiplier}
                            onChange={(e) => handleSettingChange('radiusMultiplier', parseFloat(e.target.value))}
                        />
                        <SliderInfo>
                            <span>0.05</span>
                            <SliderValue>{appState.settings.radiusMultiplier}</SliderValue>
                            <span>2</span>
                        </SliderInfo>
                    </SliderContainer>

                    <SliderContainer>
                        <label htmlFor="smoothness-slider">Contour Smoothness</label>
                        <Slider
                            type="range"
                            id="smoothness-slider"
                            min="0"
                            max="100"
                            step="1"
                            value={appState.settings.contourSmoothness}
                            onChange={(e) => handleSettingChange('contourSmoothness', parseInt(e.target.value))}
                        />
                        <SliderInfo>
                            <span>0</span>
                            <SliderValue>{appState.settings.contourSmoothness}</SliderValue>
                            <span>100</span>
                        </SliderInfo>
                    </SliderContainer>

                    <SliderContainer>
                        <label htmlFor="shades-slider">Number of Shades</label>
                        <Slider
                            type="range"
                            id="shades-slider"
                            min="3"
                            max="50"
                            step="1"
                            value={appState.settings.numShades}
                            onChange={(e) => handleSettingChange('numShades', parseInt(e.target.value))}
                        />
                        <SliderInfo>
                            <span>3</span>
                            <SliderValue>{appState.settings.numShades}</SliderValue>
                            <span>50</span>
                        </SliderInfo>
                    </SliderContainer>

                    <ColorPickerContainer>
                        <label htmlFor="low-color">Low Color</label>
                        <ColorInput
                            type="color"
                            id="low-color"
                            value={appState.settings.lowColor}
                            onChange={(e) => handleSettingChange('lowColor', e.target.value)}
                        />
                    </ColorPickerContainer>

                    <ColorPickerContainer>
                        <label htmlFor="high-color">High Color</label>
                        <ColorInput
                            type="color"
                            id="high-color"
                            value={appState.settings.highColor}
                            onChange={(e) => handleSettingChange('highColor', e.target.value)}
                        />
                    </ColorPickerContainer>
                </ControlSection>
            </ControlPanel>

            {/* Main Content */}
            <MainContent>
                <MapContainer>
                    <MapElement id="map" />
                    <Legend>
                        <LegendGradient id="legend-gradient" />
                        <LegendLabels>
                            <span>Low Rating</span>
                            <span>High Rating</span>
                        </LegendLabels>
                    </Legend>
                    <DownloadContainer>
                        <Button
                            onClick={downloadSVG}
                            style={{ marginTop: '10px' }}
                        >
                            Download SVG
                        </Button>
                    </DownloadContainer>
                    <ChatAssistant dataSummary={prepareAnswersForChatbot()} />
                </MapContainer>
            </MainContent>
        </Container>
    );
};

export default Heatmap2;